import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import Typography from "antd/es/typography";
import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { LoadingSpinner } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import { StandardFilterButton } from "@app/component/filtering";
import SelectColumnsButton from "@app/component/SelectColumnsPane/SelectColumnsButton";
import { skipNonExportFields } from "@app/component/SelectColumnsPane/util/fieldFilter";
import useHiddenColumnsFromEntityTypeAndRole from "@app/scene/reports/utils/useHiddenColumnsFromEntityTypeAndRole";
import { areCheckInFieldsVisible, isCurrentUserMember } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import FilterOperatorAction from "@app/types/FilterOperatorAction";
import Report from "@app/types/Report";
import { getEntityTypeDisplayName } from "@app/util/ui";

import {
  changeReportDescription,
  changeReportName,
  changeReportType,
  fetchPreviewData,
  fetchReport,
} from "../../store/actions";
import {
  getCurrentReport,
  getPreviewColumnDefs,
  getPreviewViewState,
  getReportTotalRowsCount,
  isLoading,
} from "../../store/selectors";

import styles from "./Common.module.scss";
import DateTimeWarning from "./DateTimeWarning";
import PreviewGrid from "./PreviewGrid";
import ReportActions from "./ReportActions";
import Toolbar from "./Toolbar";

type Props = {
  reportId: Report["id"];
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const EditReport: React.FC<Props> = ({
  areCheckInFieldsVisible,
  changeReportDescription,
  changeReportName,
  columnDefs,
  fetchReport,
  isMember,
  loading,
  previewFetcher,
  previewViewState,
  report,
  reportId,
  reportTotalRowCount,
}) => {
  const intl = useIntl();

  useEffect(() => {
    // only fetch report if it's not set already
    if (!report || report.id !== reportId) {
      fetchReport(reportId);
    }
  }, [fetchReport, report, reportId]);

  const hiddenFields = useHiddenColumnsFromEntityTypeAndRole(report?.tableName, isMember);

  const fieldFilter = useCallback(
    (field: IField) =>
      skipNonExportFields(field) &&
      (areCheckInFieldsVisible || !field.hasFeature(FieldFeature.CHECK_IN)),
    [areCheckInFieldsVisible]
  );

  const filterOperators: FilterOperatorAction = useCallback(
    (field, defaultOperators) =>
      report?.tableName === EntityType.DEAL && field.hasFeature(FieldFeature.FUNNEL_FIELD)
        ? defaultOperators.filter(({ operator }) => operator !== FilterOperator.EQUALS)
        : defaultOperators,
    [report?.tableName]
  );

  return loading || !report ? (
    <LoadingSpinner global />
  ) : (
    <section className={styles.container}>
      <ExtendedAnalytics keyPath={["Edit Report"]}>
        <PreviewGrid
          className={styles.previewGrid}
          columnDefs={columnDefs}
          previewFetcher={previewFetcher}
          previewViewState={previewViewState}
          report={report}
          reportTotalRowCount={reportTotalRowCount}
          toolbar={
            <div className={styles.topContainer}>
              <section className={styles.header}>
                <div>
                  <Typography.Title className={styles.title} level={3}>
                    {intl.formatMessage({
                      id: "reports.exports.edit.header.title",
                      defaultMessage: "Edit export",
                      description: "Header on the Edit Export page",
                    })}
                  </Typography.Title>

                  {report.tableName && (
                    <Tag color="processing">
                      {getEntityTypeDisplayName(intl, report.tableName, {
                        lowercase: false,
                        plural: true,
                      })}
                    </Tag>
                  )}
                </div>
              </section>
              <section className={styles.subHeader}>
                <Toolbar
                  onDescriptionChange={changeReportDescription}
                  onNameChange={changeReportName}
                  report={report}
                />
                <Row gutter={layout.spacerM}>
                  <Col>
                    <StandardFilterButton
                      context={`${report.tableName!}ExportPage`}
                      entityType={report.tableName!}
                      filterOperators={filterOperators}
                      hiddenFields={hiddenFields}
                      hideSavedFilters={report.tableName === EntityType.TRIP}
                    />
                  </Col>
                  <Col>
                    <SelectColumnsButton entityType={report.tableName!} fieldFilter={fieldFilter} />
                  </Col>
                  <Col>
                    <ReportActions className={styles.actions} />
                  </Col>
                </Row>
              </section>
              <DateTimeWarning entityType={report.tableName!} />
            </div>
          }
        />
      </ExtendedAnalytics>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  areCheckInFieldsVisible: areCheckInFieldsVisible(state),
  columnDefs: getPreviewColumnDefs(state)(false),
  isMember: isCurrentUserMember(state),
  loading: isLoading(state),
  previewViewState: getPreviewViewState(state),
  report: getCurrentReport(state),
  reportTotalRowCount: getReportTotalRowsCount(state),
});

const mapDispatchToProps = {
  changeReportDescription,
  changeReportName,
  changeReportType,
  fetchReport: fetchReport.request,
  previewFetcher: fetchPreviewData.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditReport);
